import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home,
  BarChart3,
  History,
  Activity,
  ChevronDown,
  Settings,
  HelpCircle,
  User,
  LogOut,
  Zap,
  ChevronLeft,
  ChevronRight,
  Clapperboard,
  Gauge,
  RefreshCw,
  Library,
  Quote
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBilling } from '../contexts/BillingContext';
import { useNotification } from '../contexts/NotificationContext';
import { useActiveRunsCount } from '../hooks/useActiveRunsCount';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DEEP_VIDEO_ANALYSIS_PATH } from '@/lib/deepVideoAnalysisRoutes';
import { BRAND_ICON_PATH, BRAND_LOGO_PATH } from '@/config/seo';

const AppSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const { userBilling, remainingCredits} = useBilling();
  const { hasNewVideos, clearNotification } = useNotification();
  const { activeCount, refresh: refreshActiveRuns, lastUpdated } = useActiveRunsCount(); // Dynamic: 5s when active, 60s when idle
  const { state, toggleSidebar } = useSidebar();


  const handleLogout = async () => {
    try {
      await logout();
      // Use a full page navigation to avoid stale chunk errors after deployments.
      window.location.assign('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isActive = (path: string) => {
    if (path === DEEP_VIDEO_ANALYSIS_PATH) {
      return location.pathname.startsWith('/analysis');
    }
    return location.pathname === path;
  };

  // Handle Dashboard click - clear notification
  const handleDashboardClick = () => {
    clearNotification();
    navigate('/dashboard');
  };

  // Main navigation items
  const mainNavItems = [
    { path: '/overview', label: 'Overview', icon: Home },
    { path: '/dashboard', label: 'Dashboard', icon: BarChart3, hasNotification: hasNewVideos },
    { path: DEEP_VIDEO_ANALYSIS_PATH, label: 'Deep Video Analysis', icon: Zap },
    { path: '/video-agent', label: 'Video Agent', icon: Clapperboard },
    { path: '/social-score', label: 'Social Video Score', icon: Gauge },
    { path: '/history', label: 'History', icon: History },
    { path: '/status', label: 'Runs', icon: Activity, badge: activeCount > 0 ? activeCount : undefined },
  ];

  const resourceItems: Array<{
    path: string;
    label: string;
    icon: typeof Library;
  }> = [{ path: '/resources/viral-hooks', label: 'Viral hooks', icon: Quote }];

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      {/* Header with Logo */}
      <SidebarHeader className="border-b border-sidebar-border">
        <button
          onClick={() => navigate('/')}
          className={`flex items-center p-2 gap-2 w-full cursor-pointer hover:bg-sidebar-accent/50 transition-all duration-300 rounded-lg group ${
            state === 'collapsed' ? 'justify-center' : 'justify-start'
          }`}
          title="Go to Welcome Page"
        >
          {/* Logo Container - responsive to collapse state */}
          <div className={`flex items-center min-w-0 ${state === 'collapsed' ? '' : 'w-full'}`}>
            {state === 'collapsed' ? (
              <img
                src={BRAND_ICON_PATH}
                alt="Mentis AI"
                className="w-10 h-10 shrink-0 object-contain group-hover:scale-110 transition-transform duration-300"
              />
            ) : (
              <img
                src={BRAND_LOGO_PATH}
                alt="Mentis AI"
                className="h-16 w-full max-w-none object-contain object-left group-hover:opacity-90 transition-opacity duration-300"
              />
            )}
          </div>
        </button>
      </SidebarHeader>

      <SidebarContent className="px-2 py-2 gap-1">
        {/* Active Runs Indicator */}
        {activeCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-2 px-3 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 rounded-lg shadow-lg shadow-cyan-500/10"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Activity className="w-4 h-4 text-cyan-400 shrink-0 animate-pulse" />
                <span className="text-xs font-semibold text-cyan-300 truncate">
                  {activeCount} {activeCount === 1 ? 'run' : 'runs'} active
                </span>
              </div>
              <div className="flex items-center gap-1">
                <motion.button
                  onClick={refreshActiveRuns}
                  className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-600/50 text-cyan-300 hover:bg-gray-600 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9, rotate: 180 }}
                  title={`Refresh (last updated: ${lastUpdated ? lastUpdated.toLocaleTimeString() : 'never'})`}
                >
                  <RefreshCw className="w-3 h-3" />
                </motion.button>
                <motion.button
                  onClick={() => navigate('/status')}
                  className="flex h-6 w-6 items-center justify-center rounded-full bg-cyan-500 text-white text-xs font-bold shrink-0 hover:bg-cyan-600 transition-colors shadow-lg shadow-cyan-500/30"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="View active runs"
                >
                  {activeCount}
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
        
        {/* Main Navigation */}
        <SidebarGroup className="p-1.5">
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavItems.map((item) => {
                const Icon = item.icon;
                const isCurrentlyActive = isActive(item.path);
                
                return (
                  <SidebarMenuItem key={item.path}>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <SidebarMenuButton
                        onClick={() => {
                          if (item.path === '/dashboard') {
                            handleDashboardClick();
                          } else {
                            navigate(item.path);
                          }
                        }}
                        isActive={isCurrentlyActive}
                        tooltip={item.label}
                        className={`
                          relative w-full transition-all duration-300 rounded-lg h-10
                          ${isCurrentlyActive 
                            ? 'bg-sidebar-accent text-sidebar-accent-foreground border-l-2 border-sidebar-primary shadow-[0_0_15px_rgba(6,182,212,0.3)]' 
                            : 'text-sidebar-foreground hover:text-sidebar-primary hover:bg-sidebar-accent/50'
                          }
                        `}
                      >
                        <div className="relative">
                          <Icon className="w-5 h-5" />
                          {item.hasNotification && (
                            <motion.div
                              className="absolute -top-1 -right-1 w-2 h-2 bg-blue-400 rounded-full shadow-lg shadow-blue-500/50"
                              animate={{ scale: [1, 1.2, 1] }}
                              transition={{ duration: 2, repeat: Infinity }}
                            />
                          )}
                        </div>
                        <span>{item.label}</span>
                        {item.badge && item.badge > 0 && (
                          <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white animate-pulse">
                            {item.badge}
                          </span>
                        )}
                      </SidebarMenuButton>
                    </motion.div>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* MENTIS Resources — above Mentis Lab so Viral hooks stays discoverable */}
        <SidebarGroup className="mt-2 p-1.5">
          {state === 'expanded' ? (
            <>
              <SidebarGroupLabel className="flex w-full items-center px-3 py-1 text-xs font-bold text-sidebar-foreground/60 uppercase tracking-widest">
                <span className="flex items-center gap-2">
                  <Library className="w-3.5 h-3.5" />
                  Resources
                </span>
              </SidebarGroupLabel>
              <SidebarGroupContent className="mt-0.5">
                <SidebarMenu>
                  {resourceItems.map((item) => {
                    const Icon = item.icon;
                    const isCurrentlyActive = isActive(item.path);
                    return (
                      <SidebarMenuItem key={item.path}>
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                          <SidebarMenuButton
                            onClick={() => navigate(item.path)}
                            isActive={isCurrentlyActive}
                            tooltip={item.label}
                            className={`
                              relative w-full transition-all duration-300 rounded-lg h-10
                              ${isCurrentlyActive
                                ? 'bg-sidebar-accent text-sidebar-accent-foreground border-l-2 border-sidebar-primary shadow-[0_0_15px_rgba(6,182,212,0.3)]'
                                : 'text-sidebar-foreground hover:text-sidebar-primary hover:bg-sidebar-accent/50'
                              }
                            `}
                          >
                            <Icon className="w-5 h-5" />
                            <span>{item.label}</span>
                          </SidebarMenuButton>
                        </motion.div>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </>
          ) : (
            <SidebarGroupContent>
              <SidebarMenu>
                {resourceItems.map((item) => {
                  const Icon = item.icon;
                  const isCurrentlyActive = isActive(item.path);
                  return (
                    <SidebarMenuItem key={item.path}>
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <SidebarMenuButton
                          onClick={() => navigate(item.path)}
                          isActive={isCurrentlyActive}
                          tooltip={item.label}
                          className={`
                            relative w-full transition-all duration-300 rounded-lg h-10
                            ${isCurrentlyActive
                              ? 'bg-sidebar-accent text-sidebar-accent-foreground border-l-2 border-sidebar-primary shadow-[0_0_15px_rgba(6,182,212,0.3)]'
                              : 'text-sidebar-foreground hover:text-sidebar-primary hover:bg-sidebar-accent/50'
                            }
                          `}
                        >
                          <Icon className="w-5 h-5" />
                          <span>{item.label}</span>
                        </SidebarMenuButton>
                      </motion.div>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          )}
        </SidebarGroup>

      </SidebarContent>

      {/* Credits always visible; settings/account collapsed by default */}
      <SidebarFooter className="border-t border-sidebar-border p-2 mt-auto">
        {currentUser && userBilling && (
          <div className="px-2 pb-2">
            <button
              type="button"
              onClick={() => navigate('/pricing')}
              title="View pricing and manage credits"
              className={cn(
                'relative flex w-full min-h-[3.125rem] cursor-pointer items-center gap-2 rounded-xl border border-sidebar-border bg-sidebar-accent px-3 py-2.5 shadow-[0_0_10px_rgba(6,182,212,0.1)] transition-all hover:bg-sidebar-accent/70 hover:shadow-[0_0_15px_rgba(6,182,212,0.2)]',
                state !== 'expanded' ? 'justify-center px-2' : ''
              )}
            >
              <span className="relative flex shrink-0 items-center justify-center text-sidebar-primary">
                <Zap className={cn(state === 'expanded' ? 'h-4 w-4' : 'h-5 w-5')} aria-hidden />
                {userBilling.currentPlan !== 'pro' &&
                  userBilling.currentPlan !== 'enterprise' &&
                  state !== 'expanded' && (
                    <span className="absolute -right-3 -top-2 flex min-h-[1rem] min-w-[1rem] items-center justify-center rounded-full bg-cyan-500 px-1 py-px text-[9px] font-bold leading-none text-white">
                      {remainingCredits > 99 ? '99+' : remainingCredits}
                    </span>
                  )}
              </span>
              {state === 'expanded' ? (
                <div className="min-w-0 flex-1 text-left">
                  <div className="flex items-baseline gap-1">
                    <span className="text-lg font-bold text-sidebar-primary-foreground">
                      {userBilling.currentPlan === 'pro' || userBilling.currentPlan === 'enterprise'
                        ? '∞'
                        : remainingCredits}
                    </span>
                    <span className="text-xs text-sidebar-foreground/60">
                      {userBilling.currentPlan === 'pro' || userBilling.currentPlan === 'enterprise'
                        ? 'Unlimited'
                        : 'credits'}
                    </span>
                  </div>
                </div>
              ) : (
                <span className="sr-only">
                  {userBilling.currentPlan === 'pro' || userBilling.currentPlan === 'enterprise'
                    ? 'Unlimited credits — view pricing'
                    : `${remainingCredits} credits — view pricing`}
                </span>
              )}
            </button>
          </div>
        )}

        <Collapsible defaultOpen={false} className="group/footer-account">
          <CollapsibleTrigger
            type="button"
            title="Show or hide settings and account"
            aria-label={state === 'expanded' ? 'Expand or collapse settings and account menu' : 'Expand or collapse settings and account'}
            className={cn(
              'flex w-full items-center gap-2 rounded-lg border border-sidebar-border/60 bg-sidebar-accent/40 text-sidebar-foreground outline-none ring-sidebar-ring transition-colors hover:bg-sidebar-accent hover:border-sidebar-primary/40 focus-visible:ring-2',
              state === 'expanded' ? 'justify-between px-3 py-2' : 'justify-center px-2 py-2'
            )}
          >
            {state === 'expanded' ? (
              <>
                <span className="flex min-w-0 flex-1 items-center gap-2 text-left text-xs font-bold uppercase tracking-widest text-sidebar-foreground/60">
                  <Settings className="h-4 w-4 shrink-0 text-sidebar-primary" />
                  <span className="truncate capitalize tracking-wide text-[11px] font-semibold normal-case">
                    Settings & account
                  </span>
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-sidebar-foreground/80 transition-transform duration-200 group-data-[state=open]/footer-account:rotate-180" />
              </>
            ) : (
              <>
                <div className="flex flex-col items-center gap-0.5 py-0.5">
                  <Settings className="h-5 w-5 shrink-0 text-sidebar-primary" />
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 text-sidebar-foreground/70 transition-transform duration-200 group-data-[state=open]/footer-account:rotate-180" />
                </div>
                <span className="sr-only">Settings & account</span>
              </>
            )}
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenu className="mt-1">
              {/* Settings */}
              <SidebarMenuItem>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SidebarMenuButton
                    onClick={() => navigate('/cabinet')}
                    tooltip="Settings"
                    className="rounded-lg text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-primary h-10"
                  >
                    <Settings className="w-5 h-5" />
                    <span>Settings</span>
                  </SidebarMenuButton>
                </motion.div>
              </SidebarMenuItem>

              {/* API Docs */}
              <SidebarMenuItem>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SidebarMenuButton
                    tooltip="API documentation coming soon"
                    className="relative cursor-not-allowed rounded-lg text-sidebar-foreground/60 transition-colors h-10"
                    disabled
                  >
                    <HelpCircle className="w-5 h-5" />
                    <span>API Docs</span>
                    <span className="ml-auto rounded-full bg-yellow-500/20 px-2 py-0.5 text-xs text-yellow-400">
                      Coming Soon
                    </span>
                  </SidebarMenuButton>
                </motion.div>
              </SidebarMenuItem>

              {/* User Profile */}
              {currentUser && (
                <>
                  <SidebarMenuItem>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <SidebarMenuButton
                        onClick={() => navigate('/cabinet')}
                        tooltip={currentUser.displayName || currentUser.email || 'Profile'}
                        className="rounded-lg text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-primary h-10"
                      >
                        <div className="flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 shadow-[0_0_10px_rgba(6,182,212,0.4)]">
                          {currentUser.photoURL ? (
                            <img 
                              src={currentUser.photoURL} 
                              alt={currentUser.displayName || 'User'} 
                              className="h-full w-full object-cover"
                              referrerPolicy="no-referrer"
                              crossOrigin="anonymous"
                              onError={(e) => {
                                const target = e.currentTarget;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  const fallback = parent.querySelector('.fallback-icon') as HTMLElement;
                                  if (fallback) fallback.classList.remove('hidden');
                                }
                              }}
                            />
                          ) : null}
                          <User className={`fallback-icon h-3 w-3 text-white ${currentUser.photoURL ? 'hidden' : ''}`} />
                        </div>
                        <span className="truncate">{currentUser.displayName || currentUser.email}</span>
                      </SidebarMenuButton>
                    </motion.div>
                  </SidebarMenuItem>

                  {/* Logout */}
                  <SidebarMenuItem>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <SidebarMenuButton
                        onClick={handleLogout}
                        tooltip="Logout"
                        className="rounded-lg text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-red-400 h-10"
                      >
                        <LogOut className="w-5 h-5" />
                        <span>Logout</span>
                      </SidebarMenuButton>
                    </motion.div>
                  </SidebarMenuItem>
                </>
              )}
            </SidebarMenu>
          </CollapsibleContent>
        </Collapsible>
      </SidebarFooter>
      
      {/* Floating Toggle Button - Always visible at the edge */}
      <div className="absolute top-3 right-0 z-50 transition-all duration-300 translate-x-1/2">
        <Button
          variant="outline"
          size="icon"
          onClick={toggleSidebar}
          className="h-8 w-8 rounded-lg border-2 border-gray-700/50 bg-gray-900 shadow-xl hover:bg-gray-800 hover:border-cyan-500/50 hover:shadow-cyan-500/20 transition-all duration-300 backdrop-blur-sm"
          aria-label="Toggle sidebar"
        >
          {state === 'expanded' ? (
            <ChevronLeft className="h-4 w-4 text-gray-300" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-300" />
          )}
        </Button>
      </div>
    </Sidebar>
  );
};

export default AppSidebar;

